import {
  AccountsService,
  MeResponse,
  meResponseSchema,
} from 'base/accounts_service';
import { zodParse } from 'base/zod_parse';
import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
} from 'mobx';
import { z } from 'zod';
import {
  getUserJwt as defaultGetUserJwt,
  logOutWithoutReload,
} from '../library/src/modules/cookies';
import { posthogIdentify } from './posthog';

const meCacheKey = 'rlOurAccountMe';
const meCacheSchema = z.object({
  timestampMs: z.number(),
  value: meResponseSchema,
});

/** Stores the "me" endpoint information about the current account */
export class AccountStore {
  private memberPromise: Promise<MeResponse> | undefined;
  @observable.deep
  private fetchedMember: MeResponse | undefined = undefined;
  private cachedMember: MeResponse | undefined;

  constructor(
    readonly service: AccountsService,
    private readonly getUserJwt = defaultGetUserJwt,
  ) {
    makeObservable(this);
    onBecomeObserved(this, 'member', () => this.get(false));
    this.cachedMember = this.getUserJwt() ? loadCachedMember() : undefined;
  }

  @computed
  get member(): MeResponse | undefined {
    return this.fetchedMember || this.cachedMember;
  }

  async get(forceRefresh: boolean = false): Promise<MeResponse | undefined> {
    if (!this.getUserJwt()) {
      return;
    }
    if (forceRefresh && this.memberPromise) {
      try {
        await this.memberPromise;
      } catch (err) {
        console.warn('Previous request failed, continuing with refresh.');
      }
    }

    if (!forceRefresh && this.memberPromise) {
      return this.memberPromise;
    }

    const promise = this.service.me();
    promise.catch((err) => {
      if (`${err}`.toLowerCase().includes('invalid token')) {
        console.info('Logging out because of invalid token', err);
        logOutWithoutReload();
      }
    });
    promise.then((data) => {
      posthogIdentify(data);
      this.fetchedMember = data;
      const cacheData: z.infer<typeof meCacheSchema> = {
        timestampMs: Date.now(),
        value: data,
      };
      try {
        localStorage.setItem(meCacheKey, JSON.stringify(cacheData));
      } catch (err) {
        console.warn('Failed to cache member data', err);
      }
      if (!data.isActive) {
        console.info('Logging out because of inactive account');
        logOutWithoutReload();
      }
    });
    this.memberPromise = promise;
    return this.memberPromise;
  }

  @action
  async setAIOptIn(value: boolean): Promise<void> {
    if (this.member?.member?.attributes == null) {
      return;
    }

    this.member.member.attributes['ai-opt-in'] = value;
    await this.service.updateAttributes({ 'ai-opt-in': value });
  }
}

function loadCachedMember(): MeResponse | undefined {
  const raw = localStorage.getItem(meCacheKey);
  if (!raw) return;
  try {
    const data = JSON.parse(raw);
    const parsed = zodParse(meCacheSchema, data);
    // only use the cached value for 7 days
    if (Date.now() - parsed.timestampMs < 7 * 24 * 60 * 60 * 1000) {
      return parsed.value;
    }
  } catch (err) {
    console.warn('Failed to parse cached member data', err);
  }
}
